import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import type { UpdateSchedulingSettingsRequest } from '@platform/contracts';
import { Alert, Card, PageHeader, Spinner, useToast } from '@platform/ui';

import { addLocalDays, formatLocalDate } from '../../features/scheduling/availability-preview';
import {
  SchedulingSettingsView,
  type AvailabilityPreviewControls,
} from '../../features/scheduling/scheduling-settings-view';
import { issuesFromApiError, type ValidationIssue } from '../../features/scheduling/validation';
import { useSchedulingClient } from '../../lib/api/scheduling-client-context';
import { describeApiError } from '../../lib/session/describe-error';

export const Route = createFileRoute('/scheduling/')({
  component: SchedulingPage,
});

/** The preview always asks for a week. */
const PREVIEW_DAYS = 7;

/**
 * `/scheduling` — booking rules and the availability preview (MASTER_PLAN M4.11 over
 * M4.3 and M4.12, `Docs/API.md` §2.7).
 *
 * The preview is `GET /admin/availability/preview` — **the server engine**, the same one
 * the booking funnel hits. It used to be computed in this browser, which meant the screen
 * could quietly disagree with what a patient was offered; the local function is still
 * there and still tested, but only as the oracle the server engine is a port of
 * (`SCHEDULING_UI.md` §3).
 *
 * The three preview controls live here rather than inside the view because they are the
 * query: changing one is a refetch, not a re-render (rules §17.8).
 */
function SchedulingPage() {
  const client = useSchedulingClient();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [saveError, setSaveError] = useState<string | undefined>(undefined);
  const [saveIssues, setSaveIssues] = useState<ValidationIssue[]>([]);
  const [query, setQuery] = useState<AvailabilityPreviewControls | null>(null);

  const settings = useQuery({
    queryKey: ['scheduling-settings'],
    queryFn: () => client.getSettings(),
  });
  const types = useQuery({
    queryKey: ['appointment-types'],
    queryFn: () => client.listAppointmentTypes(),
  });
  const providers = useQuery({ queryKey: ['providers'], queryFn: () => client.listProviders() });

  /*
   * The first query the page asks: the first day the booking window is open, the first
   * visit type and the first state the clinic serves. It cannot be state's initial value
   * because the settings it is derived from arrive a round trip later.
   */
  const defaults: AvailabilityPreviewControls | null =
    settings.data === undefined || types.data === undefined
      ? null
      : {
          from: addLocalDays(
            formatLocalDate(settings.dataUpdatedAt, settings.data.timeZone),
            settings.data.minLeadDays,
          ),
          type: types.data[0]?.id ?? '',
          state: settings.data.statesServed[0] ?? 'CA',
        };
  const effectiveQuery = query ?? defaults;

  /* A clinic with no visit types has nothing to ask about, and must not sit on a spinner. */
  const canPreview = effectiveQuery !== null && effectiveQuery.type !== '';

  const preview = useQuery({
    queryKey: ['availability-preview', effectiveQuery],
    enabled: canPreview,
    queryFn: () => {
      /* `enabled` already proved this; the closure has to say so as well. */
      if (effectiveQuery === null) throw new Error('no preview query');
      return client.getAvailabilityPreview({
        from: effectiveQuery.from,
        to: addLocalDays(effectiveQuery.from, PREVIEW_DAYS - 1),
        type: effectiveQuery.type,
        state: effectiveQuery.state,
      });
    },
  });

  const save = useMutation({
    mutationFn: (patch: UpdateSchedulingSettingsRequest) => client.saveSettings(patch),
    onSuccess: async () => {
      setSaveError(undefined);
      setSaveIssues([]);
      toast({ tone: 'success', message: 'Scheduling settings saved.' });
      await queryClient.invalidateQueries({ queryKey: ['scheduling-settings'] });
      /* The rules that were just saved are what the engine answers with. */
      await queryClient.invalidateQueries({ queryKey: ['availability-preview'] });
    },
    onError: (error: unknown) => {
      setSaveError(describeApiError(error));
      setSaveIssues(issuesFromApiError(error));
    },
  });

  if (
    settings.isPending ||
    types.isPending ||
    providers.isPending ||
    (canPreview && preview.isPending)
  ) {
    return (
      <>
        <PageHeader title="Scheduling" />
        <Card className="p-6">
          <Spinner label="Loading scheduling settings" />
        </Card>
      </>
    );
  }

  if (settings.isError || types.isError || providers.isError || preview.isError) {
    /* A refused preview is still a refusal about this page, not a partial render of it. */
    return (
      <>
        <PageHeader title="Scheduling" />
        <Alert tone="error">Scheduling could not be loaded. Try again in a moment.</Alert>
      </>
    );
  }

  if (effectiveQuery === null) {
    return (
      <>
        <PageHeader title="Scheduling" />
        <Alert tone="error">Scheduling could not be loaded. Try again in a moment.</Alert>
      </>
    );
  }

  return (
    /* `dataUpdatedAt` is when this data was fetched — the clock, without an ambient one. */
    <SchedulingSettingsView
      key={settings.dataUpdatedAt}
      settings={settings.data}
      appointmentTypes={types.data}
      providers={providers.data}
      preview={
        preview.data ?? {
          data: [],
          clinicTimeZone: settings.data.timeZone,
          busyBlocksSyncedAt: null,
        }
      }
      previewQuery={effectiveQuery}
      onPreviewQueryChange={setQuery}
      previewPending={preview.isFetching}
      saving={save.isPending}
      saveError={saveError}
      serverIssues={saveIssues}
      onSaveSettings={(patch) => {
        save.mutate(patch);
      }}
    />
  );
}
